import { type OpenShift } from "@clipboard-health/contract-worker-app-bff";
import { Text } from "@clipboard-health/ui-components";
import { Box, Radio, Stack } from "@mui/material";

import { ShiftInfoListItemContent } from "../../Bundle/BottomSheet/ShiftInfoListItemContent";
import { type TimeSlot as TimeSlotType } from "../../Open/types";
import { TimeSlotPill } from "../../TimeSlotPill";
import type { DoubleShiftPay } from "../types";

interface CompanionCardProps {
  companion: OpenShift;
  anchorStart: string;
  isSelectable: boolean;
  isSelected: boolean;
  pay?: DoubleShiftPay;
  workplaceTimezone?: string;
  onSelect: () => void;
  onShiftPress: () => void;
}

export function CompanionCard(props: CompanionCardProps) {
  const {
    companion,
    anchorStart,
    isSelectable,
    isSelected,
    pay,
    workplaceTimezone,
    onSelect,
    onShiftPress,
  } = props;

  const isBefore = companion.attributes.start < anchorStart;
  const label = isBefore ? "Before your shift" : "After your shift";

  return (
    <Box
      component="button"
      type="button"
      sx={(theme) => {
        const selected = isSelectable && isSelected;
        const unselected = isSelectable && !isSelected;
        return {
          display: "block",
          width: "100%",
          padding: 0,
          border: selected
            ? `2px solid ${theme.palette.primary.main}`
            : `1px solid ${unselected ? theme.palette.grey[200] : theme.palette.grey[300]}`,
          margin: selected ? "-1px" : 0,
          borderRadius: `${theme.shape?.borderRadius ?? 2}px`,
          backgroundColor: theme.palette.common.white,
          boxShadow: selected
            ? "0px 4px 12px rgba(0, 0, 0, 0.08)"
            : "0px 2px 8px rgba(0, 0, 0, 0.04)",
          cursor: "pointer",
          transition: "all 0.15s ease-in-out",
          overflow: "hidden",
          "&:focus-visible": {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
          },
        };
      }}
      onClick={() => {
        if (isSelectable && !isSelected) {
          onSelect();
        } else {
          onShiftPress();
        }
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={(theme) => ({
          paddingX: 5,
          paddingY: 3,
          backgroundColor: theme.palette.background.quaternary,
          borderBottom: `1px solid ${theme.palette.grey[200]}`,
        })}
      >
        <Text semibold variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
          {label}
        </Text>

        {isSelectable && (
          <Radio
            checked={isSelected}
            size="small"
            sx={{ padding: 0 }}
            inputProps={{ "aria-label": label }}
          />
        )}
      </Stack>

      <Stack spacing={3} sx={{ paddingX: 5, paddingY: 4 }}>
        <TimeSlotPill timeSlot={companion.attributes.timeSlot as TimeSlotType} />
        <Stack direction="row" alignItems="center">
          <ShiftInfoListItemContent
            timeSlot={companion.attributes.timeSlot}
            startIso={companion.attributes.start}
            endIso={companion.attributes.end}
            payInMinorUnits={pay?.totalPayInMinorUnits}
            workplaceTimezone={workplaceTimezone}
          />
        </Stack>
      </Stack>
    </Box>
  );
}
